import { useHandler } from "@redotech/react-util/hook";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import * as styles from "./quantity-increment-decrement.module.css";

export const QuantityIncrementDecrement = memo(
  function QuantityIncrementDecrement({
    value,
    setValue,
    max,
    min,
    title,
    subtitle,
    compact = false,
    size = "lg",
    disabled = false,
    numberColor = "#FF4405",
  }: {
    value: number;
    setValue(value: number): void;
    max?: number;
    min: number;
    title?: string;
    subtitle?: string;
    compact?: boolean;
    size?: "lg" | "sm";
    disabled?: boolean;
    numberColor?: string;
  }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value.toString());
    const inputRef = useRef<HTMLInputElement>(null);
    const editStartValue = useRef(value);
    const committedDuringEdit = useRef(false);

    const handleNumberClick = useHandler(() => {
      editStartValue.current = value;
      committedDuringEdit.current = false;
      setIsEditing(true);
      setEditValue(value.toString());
    });

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    const hasMax = max !== undefined;

    const handleIncrement = useHandler(() => {
      if (hasMax) {
        setValue(Math.min(value + 1, max));
        return;
      }

      setValue(value + 1);
    });

    const handleDecrement = useHandler(() => {
      setValue(Math.max(value - 1, min));
    });

    const clampValue = useHandler((raw: string): number | null => {
      const parsed = parseInt(raw, 10);
      if (isNaN(parsed)) {
        return null;
      }
      const floored = Math.max(parsed, min);
      return hasMax ? Math.min(floored, max) : floored;
    });

    const handleEditChange = useHandler((raw: string) => {
      setEditValue(raw);
      const clamped = clampValue(raw);
      if (clamped !== null) {
        committedDuringEdit.current = true;
        setValue(clamped);
      }
    });

    const handleEditSubmit = useHandler(() => {
      const clamped = clampValue(editValue);
      if (clamped !== null) {
        setValue(clamped);
      }
      setIsEditing(false);
    });

    const handleBlur = useHandler((_e: React.FocusEvent<HTMLInputElement>) => {
      flushSync(() => {
        handleEditSubmit();
      });
    });

    const handleEditCancel = useHandler(() => {
      if (committedDuringEdit.current) {
        setValue(editStartValue.current);
      }
      setIsEditing(false);
      setEditValue(editStartValue.current.toString());
    });

    const handleEditKeyDown = useHandler((e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleEditSubmit();
      } else if (e.key === "Escape") {
        handleEditCancel();
      }
    });

    const isSmall = size === "sm";

    return (
      <Flex
        borderRadius="lg"
        p={isSmall ? "none" : compact ? "md" : "3xl"}
        w={isSmall ? "fit-content" : "full"}
      >
        <Flex dir="column" gap={compact ? "sm" : "md"} w="full">
          {title && (
            <Text fontSize={compact ? "sm" : "md"} textAlign="center">
              {title}
            </Text>
          )}
          <Flex align="center" justify="center" w="full">
            <Flex align="center" gap={isSmall ? "xs" : "md"}>
              <RedoButton
                disabled={disabled || value <= min}
                hierarchy="secondary"
                IconLeading={isSmall ? undefined : MinusIcon}
                onClick={handleDecrement}
                size={isSmall ? "sm" : "lg"}
                style={
                  isSmall
                    ? undefined
                    : {
                        borderRadius: "var(--border-radius-full)",
                        width: "56px",
                        height: "56px",
                        minWidth: "56px",
                        padding: "0",
                      }
                }
                text={isSmall ? "-" : undefined}
              />

              <Flex
                align="center"
                justify="center"
                onClick={!isEditing ? handleNumberClick : undefined}
                px={isSmall ? "none" : "xl"}
                style={{
                  cursor: isEditing ? "text" : "pointer",
                  minWidth: isSmall ? "56px" : compact ? "96px" : "128px",
                }}
              >
                {isEditing ? (
                  <Flex
                    className={classNames(
                      styles.editInput,
                      isSmall
                        ? styles.editInputSmall
                        : compact
                          ? styles.editInputCompact
                          : styles.editInputLarge,
                    )}
                    style={isSmall ? undefined : { color: numberColor }}
                  >
                    <RedoTextInput
                      autoCompleteDisabled
                      htmlPattern="[0-9]*"
                      inputMode="numeric"
                      inputTypeHint="text"
                      onBlur={handleBlur}
                      onKeyDown={handleEditKeyDown}
                      ref={inputRef}
                      setValue={handleEditChange}
                      size={isSmall ? "sm" : "lg"}
                      value={editValue}
                    />
                  </Flex>
                ) : (
                  <Text
                    fontSize={isSmall ? "xl" : "2xl"}
                    fontWeight={isSmall ? "semibold" : "bold"}
                    style={
                      isSmall
                        ? undefined
                        : {
                            lineHeight: compact ? "56px" : "88px",
                            fontSize: compact ? "48px" : "72px",
                            color: numberColor,
                          }
                    }
                    textAlign="center"
                  >
                    {value}
                  </Text>
                )}
              </Flex>
              <RedoButton
                disabled={disabled || (hasMax && value >= max)}
                hierarchy="secondary"
                IconLeading={isSmall ? undefined : PlusIcon}
                onClick={handleIncrement}
                size={isSmall ? "sm" : "lg"}
                style={
                  isSmall
                    ? undefined
                    : {
                        borderRadius: "var(--border-radius-full)",
                        width: "56px",
                        height: "56px",
                        minWidth: "56px",
                        padding: 0,
                      }
                }
                text={isSmall ? "+" : undefined}
              />
            </Flex>
          </Flex>
          {subtitle && (
            <Text
              fontSize="xs"
              style={{ whiteSpace: "pre-line" }}
              textAlign="center"
              textColor="disabled"
            >
              {subtitle}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  },
);
